// eslint configured: rule not present in CRA build
import { useEffect, useState } from "react";
import { Plus, Trash2, ExternalLink, Bookmark } from "lucide-react";
import { apiGet, client } from "@/lib/api";
import SwipeModal from "@/components/SwipeModal";
import EmptyState from "@/components/EmptyState";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const INK_55 = "var(--ink-55)";

// Fallback YouTube thumbnail when an older record has no image_url stored.
function youtubeThumb(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([\w-]{11})/);
  return m ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg` : null;
}

function thumbFor(item) {
  if (item.image_url) {
    return item.image_url.startsWith("/api") ? `${BACKEND_URL}${item.image_url}` : item.image_url;
  }
  return youtubeThumb(item.source_url);
}

export default function Swipe() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);

  const load = async () => setItems(await apiGet("/swipe"));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this swipe?")) return;
    await client.delete(`/swipe/${id}`);
    load();
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
        <div>
          <div className="eyebrow"><span className="seal-dot mr-2 align-middle" /> ii. The Swipe File</div>
          <h1 className="font-heading mt-4 text-4xl md:text-5xl" style={{ fontVariationSettings: "'opsz' 96, 'wght' 450" }}>
            References, <span className="font-display-italic">indexed.</span>
          </h1>
        </div>
        <button className="btn-primary" onClick={() => setShowNew(true)} data-testid="swipe-new-btn">
          <Plus size={14} /> Add
        </button>
      </div>

      {items.length === 0 ? (
        <EmptyState
          testid="swipe-empty"
          icon={Bookmark}
          eyebrow="The Swipe File"
          title="Save what"
          titleItalic="inspires you."
          body="Drop screenshots, hooks, thumbnails, links. PintaFlow auto-fetches titles & previews — your taste library, indexed."
          primaryLabel="Add your first swipe"
          onPrimary={() => setShowNew(true)}
        />
      ) : (
        <div data-testid="swipe-grid" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map((s) => (
            <div
              key={s.id}
              data-testid={`swipe-card-${s.id}`}
              className="rounded-lg overflow-hidden group transition-colors flex flex-col"
              style={{ background: "var(--surface)", border: "1px solid var(--ink-22)" }}
            >
              <div
                className="w-full aspect-video relative overflow-hidden flex-shrink-0"
                style={{ background: "var(--bg)" }}
              >
                {thumbFor(s) ? (
                  <img
                    src={thumbFor(s)}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center" style={{ color: INK_55 }}>
                    <Bookmark size={28} strokeWidth={1.2} />
                  </div>
                )}
              </div>
              <div className="p-3 flex-1 flex flex-col">
                {s.title && (
                  <div className="leading-snug line-clamp-2" style={{ fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 32, 'wght' 450" }}>
                    {s.title}
                  </div>
                )}
                {s.note && <div className="text-xs mt-1 line-clamp-2" style={{ color: INK_55 }}>{s.note}</div>}
                <div className="flex flex-wrap gap-1 mt-2">
                  {(s.tags || []).map((t) => <span key={t} className="tag">#{t}</span>)}
                </div>
                <div className="flex items-center justify-between mt-auto pt-3 gap-2">
                  {s.source_url ? (
                    <a
                      href={s.source_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full transition hover:opacity-90"
                      style={{ background: "rgba(216,168,87,0.10)", color: "var(--seal)", border: "1px solid rgba(216,168,87,0.30)", touchAction: "manipulation" }}
                      data-testid={`swipe-open-${s.id}`}
                    >
                      <ExternalLink size={11} /> Open
                    </a>
                  ) : <span />}
                  <button
                    onClick={() => del(s.id)}
                    className="btn-ghost text-xs opacity-60 hover:opacity-100 transition"
                    data-testid={`swipe-delete-${s.id}`}
                    style={{ color: "#c97a6a", touchAction: "manipulation" }}
                    aria-label="Delete swipe"
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew && <SwipeModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}
